import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Avatar,
  IconButton,
  FormGroup,
  useTheme,
  alpha,
  Card,
  CardContent,
  Tooltip,
  Zoom,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  PhotoCamera,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
  CreditCard as CreditCardIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Visibility,
  VisibilityOff,
  Security as SecurityIcon,
  AccountCircle as AccountIcon,
  Work as WorkIcon,
  Groups as PeopleIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import FeedbackMessage from '../../common/FeedbackMessage';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import SectionCard from '../../common/SectionCard';
import { glassCardSx, ACCENT, TEXT_PRIMARY, TEXT_SECONDARY, SUCCESS } from '../../common/SaaSTokens';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import api from '../../../services/api';
import StorageAvatar from '../../common/StorageAvatar';
import {
  canEditUser as canEditUserAccess,
  canEditUserOrgAssignment,
  canManageUserPrivileges,
} from '../../../utils/userPermissions';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
} from '../../../utils/formValidationFeedback';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  50:  '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
  950: '#172554',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse    { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in  { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping     { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
`;

const MotionCard = motion(Card);

const monoSx = (color = alpha(NEON, 0.55), size = '0.58rem') => ({
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: size,
  fontWeight: 600,
  letterSpacing: 1.8,
  textTransform: 'uppercase',
  color,
  lineHeight: 1,
});

// ─── StyledField ──────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.9rem',
        color: alpha('#E2E8F0', 0.85),
        background: alpha(SURFACE_UP, 0.7),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
        '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}`,
          animation: 'form-glow-in 0.3s ease',
        },
        '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-error fieldset': { borderColor: 'rgba(248, 113, 113, 0.75) !important' },
        '&.Mui-error:hover fieldset': { borderColor: '#F87171 !important' },
        '&.Mui-error.Mui-focused fieldset': {
          borderColor: '#EF4444 !important',
          borderWidth: '1.5px',
          boxShadow: `0 0 0 3px ${alpha('#EF4444', 0.12)}`,
        },
      },
      '& .MuiInputBase-input': {
        color: alpha('#E2E8F0', 0.85),
        '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
      },
      '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
      '& .MuiInputLabel-root.Mui-error': { color: '#F87171 !important' },
      '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
      '& .MuiInputLabel-root.Mui-required::after': { color: '#EF4444', fontWeight: 700 },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
      '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      '& .MuiFormHelperText-root': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: alpha(NEON, 0.5) },
      '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
      '& .MuiOutlinedInput-root .MuiSelect-select': { color: alpha('#E2E8F0', 0.85) },
      ...sx,
    }}
  />
);

// ─── Feedback styles ──────────────────────────────────────────────────────────
const FEEDBACK_SUCCESS_SX = {
  fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
  background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`,
  border: `1px solid ${alpha(NEON3, 0.35)}`,
  boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`,
  color: alpha(NEON3, 0.95),
};
const FEEDBACK_ERROR_SX = {
  fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem',
  background: alpha('#450a0a', 0.92),
  border: '1px solid rgba(248,113,113,0.3)',
  color: '#fca5a5',
  boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
};

const PHONE_PATTERN = /^[\d\-\+\(\)\s]+$/;

const USER_VALIDATION_FIELDS = new Set([
  'name', 'email', 'password', 'profile_picture', 'course', 'matric_number',
  'phone_number', 'ic_number', 'passport_number', 'bureau_id', 'department_id', 'management_id',
  'is_admin', 'status',
]);

const normalizeOrgList = (response) => {
  const data = response?.data;
  return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
};

// ─── PermissionToggleRow ──────────────────────────────────────────────────────
const PermissionToggleRow = ({
  icon,
  label,
  description,
  checked,
  onChange,
  danger = false,
  badge = null,
  disabled = false,
}) => {
  const isActive = checked;

  const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 16px',
    borderRadius: '10px',
    border: `1px solid ${
      isActive
        ? danger
          ? 'rgba(248,113,113,0.35)'
          : alpha(NEON, 0.35)
        : alpha(NEON, 0.1)
    }`,
    background: isActive
      ? danger
        ? 'rgba(248,113,113,0.05)'
        : alpha(NEON, 0.06)
      : alpha(SURFACE_UP, 0.6),
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition: 'all 0.2s ease',
    '&:hover': disabled ? {} : {
      borderColor: danger ? 'rgba(248,113,113,0.4)' : alpha(NEON, 0.3),
      background: isActive
        ? danger
          ? 'rgba(248,113,113,0.07)'
          : alpha(NEON, 0.08)
        : alpha(NEON, 0.03),
    },
  };

  const iconSx = {
    width: 36,
    height: 36,
    borderRadius: '9px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${
      isActive
        ? danger
          ? 'rgba(248,113,113,0.45)'
          : alpha(NEON, 0.45)
        : alpha(NEON, 0.2)
    }`,
    background: isActive
      ? danger
        ? 'rgba(248,113,113,0.15)'
        : alpha(NEON, 0.18)
      : alpha(NEON, 0.08),
    color: isActive
      ? danger
        ? '#f87171'
        : NEON
      : alpha(NEON, 0.5),
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': { fontSize: 17 },
  };

  // Neon toggle thumb glow color
  const thumbGlow = danger ? 'rgba(248,113,113,0.6)' : alpha(NEON, 0.7);
  const thumbColor = danger ? '#f87171' : NEON;
  const trackBg = isActive
    ? danger
      ? 'rgba(248,113,113,0.2)'
      : alpha(NEON, 0.2)
    : alpha(NEON, 0.1);
  const trackBorder = isActive
    ? danger
      ? 'rgba(248,113,113,0.6)'
      : alpha(NEON, 0.6)
    : alpha(NEON, 0.25);

  return (
    <Box sx={rowSx} onClick={() => { if (!disabled) onChange(!checked); }}>
      {/* Icon */}
      <Box sx={iconSx}>{icon}</Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.875rem',
            fontWeight: 500,
            color: alpha('#E2E8F0', 0.85),
          }}>
            {label}
          </Typography>
          {badge}
        </Box>
        <Typography sx={{
          ...monoSx(
            isActive
              ? danger ? 'rgba(248,113,113,0.5)' : alpha(NEON, 0.5)
              : alpha(NEON, 0.35),
            '0.65rem',
          ),
          mt: 0.3,
          letterSpacing: 0.4,
          textTransform: 'none',
          fontWeight: 400,
        }}>
          {description}
        </Typography>
      </Box>

      {/* Toggle switch */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ flexShrink: 0, position: 'relative', width: 44, height: 24 }}
      >
        <Box
          component="input"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          sx={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
        />
        {/* Track */}
        <Box
          onClick={() => { if (!disabled) onChange(!checked); }}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '12px',
            background: trackBg,
            border: `1px solid ${trackBorder}`,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.25s ease',
          }}
        >
          {/* Thumb */}
          <Box sx={{
            position: 'absolute',
            top: '3px',
            left: checked ? '23px' : '3px',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: checked ? thumbColor : alpha(NEON, 0.45),
            boxShadow: checked ? `0 0 10px ${thumbGlow}` : `0 0 6px ${alpha(NEON, 0.3)}`,
            transition: 'left 0.25s ease, background 0.25s ease, box-shadow 0.25s ease',
          }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UserForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: '', email: '', password: '', profile_picture: null,
    course: '', matric_number: '', phone_number: '', ic_number: '', passport_number: '',
    bureau_id: '', department_id: '', management_id: '',
    is_admin: false, status: 'active',
  });

  const [previewUrl, setPreviewUrl]             = useState(null);
  const [existingProfilePath, setExistingProfilePath] = useState(null);
  const [profilePictureRemoved, setProfilePictureRemoved] = useState(false);
  const [bureaus, setBureaus]                   = useState([]);
  const [departments, setDepartments]           = useState([]);
  const [managements, setManagements]           = useState([]);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [successMessage, setSuccessMessage]     = useState('');
  const [showPassword, setShowPassword]         = useState(false);
  const [dataLoading, setDataLoading]           = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [faceRegistrationFeedback, setFaceRegistrationFeedback] = useState({ message: '', severity: null });
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest, setPendingUpdateRequest]       = useState(null);
  const [existingAdminUserId, setExistingAdminUserId]         = useState(null);

  const navigate = useNavigate();
  const { user: currentUser, checkAuth, logout } = useAuth();

  const canEdit = canEditUserAccess(currentUser, parseInt(id));
  const canManagePermissions = canManageUserPrivileges(currentUser);
  const canManageStatus = canManageUserPrivileges(currentUser);
  const isOwner = currentUser?.id === parseInt(id);
  const canEditOrgFields = canEditUserOrgAssignment(currentUser, isEditMode ? parseInt(id) : null);
  const isSoleAdmin = Boolean(
    formData.is_admin
    && existingAdminUserId
    && isEditMode
    && Number(id) === Number(existingAdminUserId),
  );
  const adminRoleDescription = isSoleAdmin
    ? 'Only one administrator is allowed. Promote another user before removing this role.'
    : existingAdminUserId && !formData.is_admin
      ? 'Only one administrator is allowed. Enabling this transfers admin from the current administrator.'
      : 'Restricted to user management, accessibility, and organization settings';

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'user',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  const bureauOptions = useMemo(
    () => [...bureaus.map((b) => ({ value: String(b.id), label: b.name }))],
    [bureaus],
  );
  const departmentOptions = useMemo(
    () => [...departments.map((d) => ({ value: String(d.id), label: d.name }))],
    [departments],
  );
  const managementOptions = useMemo(
    () => [...managements.map((m) => ({ value: String(m.id), label: m.name }))],
    [managements],
  );

  const handleOrgFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        const [bureausRes, departmentsRes, managementsRes, usersRes, userRes] = await Promise.allSettled([
          canEditOrgFields ? api.get('/bureaus') : Promise.resolve(null),
          canEditOrgFields ? api.get('/departments') : Promise.resolve(null),
          canEditOrgFields ? api.get('/managements') : Promise.resolve(null),
          canManagePermissions ? api.get('/users?admin_only=1') : Promise.resolve(null),
          isEditMode ? api.get(`/users/${id}`) : Promise.resolve(null),
        ]);

        if (canEditOrgFields) {
          setBureaus(bureausRes.status === 'fulfilled' ? normalizeOrgList(bureausRes.value) : []);
          setDepartments(departmentsRes.status === 'fulfilled' ? normalizeOrgList(departmentsRes.value) : []);
          setManagements(managementsRes.status === 'fulfilled' ? normalizeOrgList(managementsRes.value) : []);
        }

        if (canManagePermissions) {
          const users = usersRes.status === 'fulfilled' && Array.isArray(usersRes.value?.data)
            ? usersRes.value.data
            : [];
          const currentAdmin = users.find((user) => Boolean(user.is_admin));
          setExistingAdminUserId(currentAdmin?.id ?? null);
        } else {
          setExistingAdminUserId(null);
        }

        if (isEditMode) {
          if (userRes.status !== 'fulfilled') throw userRes.reason;
          const userData = userRes.value.data;
          setFormData({
            name: userData.name || '', email: userData.email || '', password: '',
            profile_picture: null, course: userData.course || '',
            matric_number: userData.matric_number || '', phone_number: userData.phone_number || '',
            ic_number: userData.ic_number || '', passport_number: userData.passport_number || '',
            bureau_id: userData.bureau_id || '',
            department_id: userData.department_id || '', management_id: userData.management_id || '',
            is_admin: userData.is_admin || false, status: userData.status || 'active',
          });
          setProfilePictureRemoved(false);
          if (userData.profile_picture) {
            setExistingProfilePath(userData.profile_picture || userData.profile_picture_url);
            setPreviewUrl(null);
          } else {
            setExistingProfilePath(null);
            setPreviewUrl(null);
          }
        }
      } catch (err) {
        setError('Failed to load form data');
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode, canEditOrgFields, canManagePermissions]);

  const handleFormApiError = (err, fallbackMessage) => {
    const data = err?.response?.data;
    applyApiValidationErrors(data, setValidationErrors, USER_VALIDATION_FIELDS);
    const fieldMsgs = {};
    const errs = data?.errors;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([key, msgs]) => {
        if (!USER_VALIDATION_FIELDS.has(key)) return;
        const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
        if (msg) fieldMsgs[key] = String(msg);
      });
    }
    if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
    setError(
      Object.keys(fieldMsgs).length
        ? formatFieldErrorsMessage(fieldMsgs)
        : (getFirstErrorMessage(data) || fallbackMessage),
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors((prev) => ({ ...prev, profile_picture: '' }));
        setError('Profile picture must be less than 5 MB.');
        return;
      }
      if (error) setError(null);
      setProfilePictureRemoved(false);
      setFormData(prev => ({ ...prev, profile_picture: file }));
      const reader = new FileReader();
      reader.onload = (event) => setPreviewUrl(event.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveProfilePicture = () => {
    setFormData(prev => ({ ...prev, profile_picture: null }));
    setPreviewUrl(null);
    setExistingProfilePath(null);
    setProfilePictureRemoved(true);
  };

  const collectValidationErrors = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!isEditMode && !formData.password) errors.password = 'Password is required';
    else if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    const phone = (formData.phone_number || '').trim();
    if (phone && !PHONE_PATTERN.test(phone)) {
      errors.phone_number = 'Invalid phone number';
    }
    return errors;
  };

  const validateForm = () => {
    const errors = collectValidationErrors();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true);
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setShowFieldErrors(false);
    setError(null);
    return true;
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read profile picture for face registration.'));
      reader.readAsDataURL(file);
    });

  const registerFaceFromProfilePicture = async (userId, profilePictureFile) => {
    if (!(profilePictureFile instanceof File)) return;
    const imageBase64 = await fileToDataUrl(profilePictureFile);
    await api.post('/face/register', { user_id: userId, image: imageBase64, save_image: false });
  };

  /** Best-effort face enrollment; never blocks user create/update. */
  const tryRegisterFaceFromProfilePicture = async (userId, profilePictureFile) => {
    if (!(profilePictureFile instanceof File) || !userId) return;
    try {
      await registerFaceFromProfilePicture(userId, profilePictureFile);
      setFaceRegistrationFeedback({
        message: 'Face registered successfully from the uploaded profile picture.',
        severity: 'success',
      });
    } catch (faceErr) {
      const detail = getFirstErrorMessage(faceErr?.response?.data);
      setFaceRegistrationFeedback({
        message: detail
          ? `Face registration failed: ${detail}`
          : 'Face registration failed. Profile was saved; try again with a clear front-facing photo.',
        severity: 'error',
      });
    }
  };

  const confirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors, setValidationErrors, setError,
        setConfirmUpdateDialogOpen, setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setLoading(true); setError(null); setValidationErrors({});
      let updatedUser = null;
      let shouldLogoutAfterTransfer = false;
      if (pendingUpdateRequest.kind === 'formdata') {
        const response = await api.post(pendingUpdateRequest.url, pendingUpdateRequest.fd);
        updatedUser = response?.data?.user || null;
        shouldLogoutAfterTransfer = Boolean(response?.data?.auth?.should_logout);
      } else {
        const response = await api.put(pendingUpdateRequest.url, pendingUpdateRequest.jsonData);
        updatedUser = response?.data?.user || null;
        shouldLogoutAfterTransfer = Boolean(response?.data?.auth?.should_logout);
      }
      if (formData.profile_picture instanceof File && updatedUser?.id) {
        await tryRegisterFaceFromProfilePicture(updatedUser.id, formData.profile_picture);
      }
      if (shouldLogoutAfterTransfer) {
        logout();
        navigate('/login', {
          replace: true,
          state: { message: 'Admin role transferred. Please log in with an active account.' },
        });
        return;
      }
      if (updatedUser?.id && Number(updatedUser.id) === Number(currentUser?.id)) await checkAuth();
      setSuccessMessage('User updated successfully! ');
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      handleFormApiError(err, 'Failed to update user.');
    } finally {
      setLoading(false);
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        setError(null); setValidationErrors({});
        const userData = { ...formData };
        if (!userData.password) delete userData.password;
        if (!canManageStatus) delete userData.status;
        if (!canManagePermissions) delete userData.is_admin;
        if (!canEditOrgFields) {
          delete userData.bureau_id;
          delete userData.department_id;
          delete userData.management_id;
        }
        if (formData.profile_picture instanceof File) {
          const fd = new FormData();
          Object.keys(userData).forEach((key) => {
            if (userData[key] == null) return;
            if (typeof userData[key] === 'boolean') { fd.append(key, userData[key] ? '1' : '0'); return; }
            if (key === 'profile_picture' && userData[key] instanceof File) { fd.append(key, userData[key]); return; }
            fd.append(key, userData[key]);
          });
          setPendingUpdateRequest({ kind: 'formdata', url: `/users/${id}`, fd });
        } else {
          const jsonData = { ...userData };
          delete jsonData.profile_picture;
          if (profilePictureRemoved) {
            jsonData.remove_profile_picture = true;
          }
          setPendingUpdateRequest({ kind: 'json', url: `/users/${id}`, jsonData });
        }
        setConfirmUpdateDialogOpen(true);
        return;
      }
      setError(null);
      if (!validateForm()) return;
      setLoading(true); setValidationErrors({});
      const userData = { ...formData };
      if (!canManageStatus) delete userData.status;
      if (!canManagePermissions) delete userData.is_admin;
      if (!canEditOrgFields) {
        delete userData.bureau_id;
        delete userData.department_id;
        delete userData.management_id;
      }
      const fd = new FormData();
      Object.keys(userData).forEach((key) => {
        if (userData[key] == null) return;
        if (typeof userData[key] === 'boolean') { fd.append(key, userData[key] ? '1' : '0'); return; }
        if (key === 'profile_picture' && userData[key] instanceof File) { fd.append(key, userData[key]); return; }
        fd.append(key, userData[key]);
      });
      const response = await api.post('/users', fd);
      if (response.data) {
        if (formData.profile_picture instanceof File && response.data.id) {
          await tryRegisterFaceFromProfilePicture(response.data.id, formData.profile_picture);
        }
        if (response.data.auth?.should_logout) {
          logout();
          navigate('/login', {
            replace: true,
            state: { message: 'Admin role transferred. Please log in with an active account.' },
          });
          return;
        }
        if (Number(response.data.id) === Number(currentUser?.id)) await checkAuth();
        if (!isEditMode) formDraft.clearDraft();
        setSuccessMessage('User created successfully! ');
        setTimeout(() => navigate('/users'), 1500);
      }
    } catch (err) {
      handleFormApiError(err, 'Failed to save user.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render guards ──────────────────────────────────────────────────────────
  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <FeedbackMessage message="Please log in to access this page." severity="error" sx={FEEDBACK_ERROR_SX} />
      </Container>
    );
  }
  if (!canEdit) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <FeedbackMessage message="You don't have permission to access this page." severity="error" sx={FEEDBACK_ERROR_SX} />
      </Container>
    );
  }
  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading user form" size={56} variant="primary" />
    </Box>
  );

  const canSubmit = Boolean(
    formData.name.trim() &&
    formData.email.trim() &&
    (isEditMode || formData.password),
  );
  const missingRequiredFields = [
    !formData.name.trim() && 'Full Name',
    !formData.email.trim() && 'Email',
    !isEditMode && !formData.password && 'Password',
  ].filter(Boolean);
  const submitStatusText = isEditMode
    ? 'Save changes to update this user.'
    : missingRequiredFields.length > 0
      ? `Required fields remaining: ${missingRequiredFields.join(', ')}.`
      : 'All required fields are complete. Ready to create user.';
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title={isEditMode ? (isOwner ? 'Edit My Profile' : 'Edit User') : 'Create New User'}
        subtitle={
          isEditMode
            ? isOwner ? 'Update your personal information and settings'
                      : 'Update user information and settings'
            : 'Add a new member to your organization'
        }
        icon={PeopleIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
      />

      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')} sx={FEEDBACK_SUCCESS_SX} />
      {faceRegistrationFeedback.message && (
        <FeedbackMessage
          message={faceRegistrationFeedback.message}
          severity={faceRegistrationFeedback.severity}
          onClose={() => setFaceRegistrationFeedback({ message: '', severity: null })}
          sx={faceRegistrationFeedback.severity === 'success' ? FEEDBACK_SUCCESS_SX : FEEDBACK_ERROR_SX}
        />
      )}
      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} sx={FEEDBACK_ERROR_SX} />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── SECTION 1: Profile + Personal Information ── */}
          <SectionCard title="Personal Information" icon={<AccountIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { md: 'flex-start' } }}>

              {/* Avatar column */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Box sx={{
                    position: 'absolute', inset: -6, borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(NEON, 0.25)}, transparent 68%)`,
                    filter: 'blur(6px)', pointerEvents: 'none',
                    animation: 'form-pulse 3s ease-in-out infinite',
                  }} />
                  {previewUrl ? (
                  <Avatar
                    src={previewUrl}
                    alt="Profile Preview"
                    sx={{
                      width: 90, height: 90,
                      background: `linear-gradient(135deg, ${alpha(NEON, 0.3)}, ${alpha(B[700], 0.6)})`,
                      border: `2.5px solid ${alpha(NEON, 0.45)}`,
                      boxShadow: `0 0 22px ${alpha(NEON, 0.35)}, 0 4px 16px ${alpha('#000', 0.5)}`,
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700, fontSize: '2rem', color: '#fff',
                      position: 'relative', zIndex: 1,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      '&:hover': {
                        transform: 'scale(1.04)',
                        boxShadow: `0 0 32px ${alpha(NEON, 0.5)}, 0 6px 20px ${alpha('#000', 0.55)}`,
                      },
                    }}
                  />
                  ) : existingProfilePath && !profilePictureRemoved ? (
                  <StorageAvatar
                    storagePath={existingProfilePath}
                    alt="Profile Preview"
                    sx={{
                      width: 90, height: 90,
                      background: `linear-gradient(135deg, ${alpha(NEON, 0.3)}, ${alpha(B[700], 0.6)})`,
                      border: `2.5px solid ${alpha(NEON, 0.45)}`,
                      boxShadow: `0 0 22px ${alpha(NEON, 0.35)}, 0 4px 16px ${alpha('#000', 0.5)}`,
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700, fontSize: '2rem', color: '#fff',
                      position: 'relative', zIndex: 1,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      '&:hover': {
                        transform: 'scale(1.04)',
                        boxShadow: `0 0 32px ${alpha(NEON, 0.5)}, 0 6px 20px ${alpha('#000', 0.55)}`,
                      },
                    }}
                  />
                  ) : (
                  <Avatar
                    alt="Profile Preview"
                    sx={{
                      width: 90, height: 90,
                      background: `linear-gradient(135deg, ${alpha(NEON, 0.3)}, ${alpha(B[700], 0.6)})`,
                      border: `2.5px solid ${alpha(NEON, 0.45)}`,
                      boxShadow: `0 0 22px ${alpha(NEON, 0.35)}, 0 4px 16px ${alpha('#000', 0.5)}`,
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: 700, fontSize: '2rem', color: '#fff',
                      position: 'relative', zIndex: 1,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      '&:hover': {
                        transform: 'scale(1.04)',
                        boxShadow: `0 0 32px ${alpha(NEON, 0.5)}, 0 6px 20px ${alpha('#000', 0.55)}`,
                      },
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  )}

                  {/* Upload button */}
                  <input accept="image/*" type="file" id="profile-picture-input" hidden onChange={handleProfilePictureChange} />
                  <Tooltip title="Upload Photo" placement="bottom-end">
                    <label htmlFor="profile-picture-input">
                      <IconButton
                        component="span"
                        sx={{
                          position: 'absolute', bottom: -4, right: -4, zIndex: 2,
                          width: 30, height: 30, borderRadius: '8px',
                          background: `linear-gradient(135deg, ${alpha(NEON, 0.25)}, ${alpha(B[700], 0.5)})`,
                          border: `1.5px solid ${alpha(NEON, 0.45)}`,
                          color: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.45)}`,
                          transition: 'all 0.22s ease',
                          '&:hover': { transform: 'scale(1.12)', boxShadow: `0 0 20px ${alpha(NEON, 0.65)}` },
                        }}
                      >
                        <UploadIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </label>
                  </Tooltip>

                  {/* Remove button */}
                  {(previewUrl || (existingProfilePath && !profilePictureRemoved)) && (
                    <Zoom in={Boolean(previewUrl)}>
                      <Tooltip title="Remove Photo" placement="bottom-start">
                        <IconButton
                          onClick={handleRemoveProfilePicture}
                          sx={{
                            position: 'absolute', top: -4, right: -4, zIndex: 2,
                            width: 30, height: 30, borderRadius: '8px',
                            background: alpha('#1a0505', 0.9), color: '#f87171',
                            border: `1.5px solid rgba(248,113,113,0.4)`,
                            boxShadow: `0 2px 8px rgba(239,68,68,0.3)`,
                            transition: 'all 0.22s ease',
                            '&:hover': { background: 'rgba(239,68,68,0.25)', color: '#fca5a5', transform: 'scale(1.12)' },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Zoom>
                  )}
                </Box>
                <Typography sx={{ ...monoSx(alpha(NEON, 0.4), '0.62rem'), letterSpacing: 0.8, textAlign: 'center' }}>
                  JPG · PNG · Max 5 MB
                </Typography>
              </Box>

              {/* Fields column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Row 1: Name + Email */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <StyledField
                    fullWidth label="Full Name" name="name" value={formData.name}
                    onChange={handleChange}
                    error={fieldHasError('name')} helperText={fieldHelper('name')}
                    required sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
                  />
                  <StyledField
                    fullWidth label="Email" name="email" type="email" value={formData.email}
                    onChange={handleChange}
                    error={fieldHasError('email')} helperText={fieldHelper('email')}
                    required sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
                  />
                </Box>
                {/* Row 2: Password + Phone Number */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <StyledField
                    fullWidth label="Password" name="password" type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange}
                    error={fieldHasError('password')}
                    helperText={fieldHelper('password') || (isEditMode ? 'Leave empty to keep current password' : 'Min. 8 characters')}
                    required={!isEditMode} sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: alpha(NEON, 0.45) }}>
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledField
                    fullWidth label="Phone Number" name="phone_number" value={formData.phone_number}
                    onChange={handleChange}
                    error={fieldHasError('phone_number')}
                    helperText={fieldHelper('phone_number')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>
              </Box>
            </Box>
          </SectionCard>

          {/* ── SECTION 2: Academic + Identification ── */}
          <SectionCard title="Academic & Identification" icon={<SchoolIcon />} delay={0.06}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <StyledField
                  fullWidth label="Course" name="course" value={formData.course}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SchoolIcon /></InputAdornment> }}
                />
                <StyledField
                  fullWidth label="Matric Number" name="matric_number" value={formData.matric_number}
                  onChange={handleChange}
                  error={fieldHasError('matric_number')} helperText={fieldHelper('matric_number')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon /></InputAdornment> }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <StyledField
                  fullWidth label="IC Number" name="ic_number" value={formData.ic_number}
                  onChange={handleChange}
                  error={fieldHasError('ic_number')} helperText={fieldHelper('ic_number')}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon /></InputAdornment> }}
                />
                <StyledField
                  fullWidth label="Passport Number" name="passport_number" value={formData.passport_number}
                  onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CreditCardIcon /></InputAdornment> }}
                />
              </Box>
            </Box>
          </SectionCard>

          {/* ── SECTION 3: Organization & Assignment — TL / HR / Admin editing others only ── */}
          {canEditOrgFields && (
            <SectionCard title="Organization & Assignment" icon={<BusinessIcon />} delay={0.12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <SearchableSelect
                    label="Bureau"
                    value={formData.bureau_id}
                    onChange={(v) => handleOrgFieldChange('bureau_id', v)}
                    options={bureauOptions}
                    error={fieldHasError('bureau_id')}
                    helperText={fieldHelper('bureau_id')}
                    startIcon={<BusinessIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                  <SearchableSelect
                    label="Department"
                    value={formData.department_id}
                    onChange={(v) => handleOrgFieldChange('department_id', v)}
                    options={departmentOptions}
                    error={fieldHasError('department_id')}
                    helperText={fieldHelper('department_id')}
                    startIcon={<WorkIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <SearchableSelect
                    label="Management Level"
                    value={formData.management_id}
                    onChange={(v) => handleOrgFieldChange('management_id', v)}
                    options={managementOptions}
                    error={fieldHasError('management_id')}
                    helperText={fieldHelper('management_id')}
                    startIcon={<WorkIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            </SectionCard>
          )}

          {/* ── SECTION 4: Permissions — admin only ── */}
          {(canManagePermissions || canManageStatus) && (
            <SectionCard title="Permissions" icon={<SecurityIcon />} delay={0.18}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                {/* Both visible: side-by-side on md+, stacked on xs */}
                {canManageStatus && canManagePermissions ? (
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>

                    {/* Account Status toggle */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ ...monoSx(alpha(NEON, 0.35), '0.6rem'), mb: 0.75 }}>
                        Account
                      </Typography>
                      <PermissionToggleRow
                        icon={<ToggleOnIcon />}
                        label="Account active"
                        description={
                          formData.status === 'active'
                            ? 'Full platform access enabled'
                            : 'Restricted — Gallery access only'
                        }
                        checked={formData.status === 'active'}
                        onChange={(val) => {
                          setFormData(prev => ({ ...prev, status: val ? 'active' : 'inactive' }));
                          if (error) setError(null);
                        }}
                        danger={formData.status !== 'active'}
                        badge={
                          <Box component="span" sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            letterSpacing: '1.2px',
                            textTransform: 'uppercase',
                            padding: '2px 8px',
                            borderRadius: '5px',
                            background: formData.status === 'active'
                              ? 'rgba(52,211,153,0.12)'
                              : 'rgba(248,113,113,0.1)',
                            color: formData.status === 'active' ? '#34D399' : '#f87171',
                            border: `1px solid ${formData.status === 'active' ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.25)'}`,
                          }}>
                            {formData.status === 'active' ? 'Active' : 'Inactive'}
                          </Box>
                        }
                      />
                    </Box>

                    {/* Administrator toggle */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ ...monoSx(alpha(NEON, 0.35), '0.6rem'), mb: 0.75 }}>
                        Roles
                      </Typography>
                      <PermissionToggleRow
                        icon={<SecurityIcon />}
                        label="Administrator"
                        description={adminRoleDescription}
                        checked={formData.is_admin}
                        disabled={isSoleAdmin}
                        onChange={(val) => {
                          if (isSoleAdmin && !val) return;
                          setFormData(prev => ({ ...prev, is_admin: val }));
                          if (error) setError(null);
                        }}
                        badge={
                          <Box component="span" sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            letterSpacing: '1.2px',
                            textTransform: 'uppercase',
                            padding: '2px 8px',
                            borderRadius: '5px',
                            background: formData.is_admin
                              ? alpha(NEON2, 0.12)
                              : alpha(NEON, 0.08),
                            color: formData.is_admin ? NEON2 : alpha(NEON, 0.6),
                            border: `1px solid ${formData.is_admin ? alpha(NEON2, 0.35) : alpha(NEON, 0.2)}`,
                          }}>
                            {formData.is_admin ? 'Admin' : 'Member'}
                          </Box>
                        }
                      />
                    </Box>

                  </Box>
                ) : (
                  /* Only one is visible — render solo, stacked */
                  <>
                    {canManageStatus && (
                      <>
                        <Typography sx={{ ...monoSx(alpha(NEON, 0.35), '0.6rem'), mb: 0.5 }}>
                          Account
                        </Typography>
                        <PermissionToggleRow
                          icon={<ToggleOnIcon />}
                          label="Account active"
                          description={
                            formData.status === 'active'
                              ? 'Full platform access enabled'
                              : 'Restricted — Gallery access only'
                          }
                          checked={formData.status === 'active'}
                          onChange={(val) => {
                            setFormData(prev => ({ ...prev, status: val ? 'active' : 'inactive' }));
                            if (error) setError(null);
                          }}
                          danger={formData.status !== 'active'}
                          badge={
                            <Box component="span" sx={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              letterSpacing: '1.2px',
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '5px',
                              background: formData.status === 'active'
                                ? 'rgba(52,211,153,0.12)'
                                : 'rgba(248,113,113,0.1)',
                              color: formData.status === 'active' ? '#34D399' : '#f87171',
                              border: `1px solid ${formData.status === 'active' ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.25)'}`,
                            }}>
                              {formData.status === 'active' ? 'Active' : 'Inactive'}
                            </Box>
                          }
                        />
                      </>
                    )}
                    {canManagePermissions && (
                      <>
                        <Typography sx={{ ...monoSx(alpha(NEON, 0.35), '0.6rem'), mb: 0.5 }}>
                          Roles
                        </Typography>
                        <PermissionToggleRow
                          icon={<SecurityIcon />}
                          label="Administrator"
                          description={adminRoleDescription}
                          checked={formData.is_admin}
                          disabled={isSoleAdmin}
                          onChange={(val) => {
                            if (isSoleAdmin && !val) return;
                            setFormData(prev => ({ ...prev, is_admin: val }));
                            if (error) setError(null);
                          }}
                        />
                      </>
                    )}
                  </>
                )}

              </Box>
            </SectionCard>
          )}

          <SubmitActionsCard
            statusText={submitStatusText}
            cancelLabel="Cancel"
            onCancel={() => navigate(-1)}
            cancelDisabled={loading}
            submitLabel={isEditMode ? 'Update User' : 'Create User'}
            submitDisabled={loading || !canSubmit}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />
        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        variant="edit"
        itemType="user"
        itemName={formData.name}
        onConfirm={confirmUpdate}
        title="Confirm Update"
        description="This action will update the user details and cannot be undone."
        confirmLabel={isEditMode ? 'Update User' : 'Create User'}
        confirmDisabled={!canSubmit}
        loading={loading}
      />
    </Container>
  );
};

export default UserForm;
